/**
 * 数据字典工具类
 */
import { useDictStoreWithOut } from '@/store/modules/dict'
import { ElementPlusInfoType } from '@/types/elementPlus'

const dictStore = useDictStoreWithOut()

/**
 * 获取 dictType 对应的数据字典数组
 *
 * @param dictType 数据类型
 * @returns {*|Array} 数据字典数组
 */
export interface DictDataType {
  dictType: string
  label: string
  value: string | number | boolean
  colorType: ElementPlusInfoType | ''
  cssClass: string
}

export interface NumberDictDataType extends DictDataType {
  value: number
}

export interface StringDictDataType extends DictDataType {
  value: string
}

export interface BooleanDictDataType extends DictDataType {
  value: boolean
}

export const getDictOptions = (dictType: string) => {
  return dictStore.getDictByType(dictType) || []
}

export const getIntDictOptions = (dictType: string): NumberDictDataType[] => {
  // 获得通用的 DictDataType 列表
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  // 转换成 number 类型的 NumberDictDataType 类型
  // why 需要特殊转换：避免 IDEA 在 v-for="dict in getIntDictOptions(...)" 时，el-option 的 key 会告警
  const dictOption: NumberDictDataType[] = []
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: parseInt(dict.value + '')
    })
  })
  return dictOption
}

export const getStrDictOptions = (dictType: string) => {
  // 获得通用的 DictDataType 列表
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  // 转换成 string 类型的 StringDictDataType 类型
  // why 需要特殊转换：避免 IDEA 在 v-for="dict in getStrDictOptions(...)" 时，el-option 的 key 会告警
  const dictOption: StringDictDataType[] = []
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: dict.value + ''
    })
  })
  return dictOption
}

export const getBoolDictOptions = (dictType: string): BooleanDictDataType[] => {
  const dictOption: BooleanDictDataType[] = []
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: dict.value + '' === 'true'
    })
  })
  return dictOption
}

/**
 * 获取指定字典类型的指定值对应的字典对象
 * @param dictType 字典类型
 * @param value 字典值
 * @return DictDataType 字典对象
 */
export const getDictObj = (dictType: string, value: any): DictDataType | undefined => {
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  for (const dict of dictOptions) {
    if (dict.value === value + '') {
      return dict
    }
  }
}

/**
 * 获得字典数据的文本展示
 *
 * @param dictType 字典类型
 * @param value 字典数据的值
 * @return 字典名称
 */
export const getDictLabel = (dictType: string, value: any): string => {
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  const dictLabel = ref('')
  dictOptions.forEach((dict: DictDataType) => {
    if (dict.value === value + '') {
      dictLabel.value = dict.label
    }
  })
  return dictLabel.value
}

export enum DICT_TYPE {
  RENTAL_ORDER_STATUS = 'rental_order_status',
  USER_TYPE = 'user_type',
  COMMON_STATUS = 'common_status',
  TERMINAL = 'terminal', // 终端
  DATE_INTERVAL = 'date_interval', // 数据间隔

  /** Boss 圈合伙人加盟档位主题 */
  BOSSCIRCLE_PARTNER_THEME = 'bosscircle_partner_theme',
  /** Boss 圈圈子展示标签（默认可选） */
  BOSSCIRCLE_CIRCLE_TAG = 'bosscircle_circle_tag',
  /** Boss 圈首页侧栏功能分组（推荐功能 / 平台功能） */
  BOSSCIRCLE_HOME_FEATURE_GROUP = 'bosscircle_home_feature_group',
  /** Boss 圈公司账号状态（启用/停用，与 common_status 同值） */
  BOSSCIRCLE_COMPANY_STATUS = 'bosscircle_company_status',
  /** Boss 圈公司成员角色 owner/employee */
  BOSSCIRCLE_COMPANY_MEMBER_ROLE = 'bosscircle_company_member_role',
  /** Boss 圈公司成员状态 bound/left */
  BOSSCIRCLE_COMPANY_MEMBER_STATUS = 'bosscircle_company_member_status',
  /** Boss 圈入企申请状态 0待审 1通过 2驳回 3待企业入驻 4已撤销 */
  BOSSCIRCLE_COMPANY_JOIN_APPLY_STATUS = 'bosscircle_company_join_apply_status',
  /** Boss 圈审核人类型 owner/admin/system */
  BOSSCIRCLE_AUDIT_SOURCE = 'bosscircle_audit_source',
  /** Boss 圈归一审核结果 pending/approved/rejected/withdrawn/cancelled */
  BOSSCIRCLE_AUDIT_RESULT = 'bosscircle_audit_result',
  /** Boss 圈企业审核轨迹类型 entry/join/member */
  BOSSCIRCLE_AUDIT_TRACE_BIZ_TYPE = 'bosscircle_audit_trace_biz_type',
  /** Boss 圈约见确认状态 0待对方确认 1已接受 2已拒绝 3已撤回 4已超时 */
  BOSSCIRCLE_VISIT_ACCEPT_STATUS = 'bosscircle_visit_accept_status',

  /** 活动举报原因 */
  ACTIVITY_REPORT_TYPE = 'activity_report_type',
  /** 活动举报处理状态 */
  ACTIVITY_REPORT_AUDIT_STATUS = 'activity_report_audit_status',

  // ========== SYSTEM 模块 ==========
  SYSTEM_USER_SEX = 'system_user_sex',
  SYSTEM_MENU_TYPE = 'system_menu_type',
  SYSTEM_ROLE_TYPE = 'system_role_type',
  SYSTEM_DATA_SCOPE = 'system_data_scope',
  SYSTEM_NOTICE_TYPE = 'system_notice_type',
  SYSTEM_LOGIN_TYPE = 'system_login_type',
  SYSTEM_LOGIN_RESULT = 'system_login_result',
  SYSTEM_SMS_CHANNEL_CODE = 'system_sms_channel_code',
  SYSTEM_SMS_TEMPLATE_TYPE = 'system_sms_template_type',
  SYSTEM_SMS_SEND_STATUS = 'system_sms_send_status',
  SYSTEM_SMS_RECEIVE_STATUS = 'system_sms_receive_status',
  SYSTEM_OAUTH2_GRANT_TYPE = 'system_oauth2_grant_type',
  SYSTEM_MAIL_SEND_STATUS = 'system_mail_send_status',
  SYSTEM_NOTIFY_TEMPLATE_TYPE = 'system_notify_template_type',
  SYSTEM_SOCIAL_TYPE = 'system_social_type',
  SYSTEM_LOGIN_SKIN_STATUS = 'system_login_skin_status', // 登录皮肤启停（0停用 1启用）
  SYSTEM_LOGIN_SKIN_BRAND = 'system_login_skin_brand', // 登录皮肤品牌：BOSS直卖/BOSS圈儿/紫金山AI
  SYSTEM_AGREEMENT_CODE = 'system_agreement_code', // 公共协议编码
  PLATFORM_DIST_LEDGER_STATUS = 'platform_dist_ledger_status', // 平台分销流水状态
  PLATFORM_DIST_WITHDRAW_STATUS = 'platform_dist_withdraw_status', // 平台分销提现状态
  PLATFORM_DIST_APPLY_STATUS = 'platform_dist_apply_status', // 平台分销入驻申请状态

  // ========== INFRA 模块 ==========
  INFRA_BOOLEAN_STRING = 'infra_boolean_string',
  INFRA_JOB_STATUS = 'infra_job_status',
  INFRA_JOB_LOG_STATUS = 'infra_job_log_status',
  INFRA_API_ERROR_LOG_PROCESS_STATUS = 'infra_api_error_log_process_status',
  INFRA_CONFIG_TYPE = 'infra_config_type',
  INFRA_CODEGEN_TEMPLATE_TYPE = 'infra_codegen_template_type',
  INFRA_CODEGEN_FRONT_TYPE = 'infra_codegen_front_type',
  INFRA_CODEGEN_SCENE = 'infra_codegen_scene',
  INFRA_FILE_STORAGE = 'infra_file_storage',
  INFRA_OPERATE_TYPE = 'infra_operate_type',

  // ========== BPM 模块 ==========
  BPM_MODEL_TYPE = 'bpm_model_type',
  BPM_MODEL_FORM_TYPE = 'bpm_model_form_type',
  BPM_TASK_CANDIDATE_STRATEGY = 'bpm_task_candidate_strategy',
  BPM_PROCESS_INSTANCE_STATUS = 'bpm_process_instance_status',
  BPM_TASK_STATUS = 'bpm_task_status',
  BPM_OA_LEAVE_TYPE = 'bpm_oa_leave_type',
  BPM_PROCESS_LISTENER_TYPE = 'bpm_process_listener_type',
  BPM_PROCESS_LISTENER_VALUE_TYPE = 'bpm_process_listener_value_type',

  // ========== PAY 模块 ==========
  PAY_CHANNEL_CODE = 'pay_channel_code', // 支付渠道编码类型
  PAY_ORDER_STATUS = 'pay_order_status', // 商户支付订单状态
  PAY_REFUND_STATUS = 'pay_refund_status', // 退款订单状态
  PAY_NOTIFY_STATUS = 'pay_notify_status', // 商户支付回调状态
  PAY_NOTIFY_TYPE = 'pay_notify_type', // 商户支付回调状态
  PAY_TRANSFER_TYPE = 'pay_transfer_type', // 转账订单类型
  PAY_TRANSFER_STATUS = 'pay_transfer_status', // 转账订单状态

  // ========== MP 模块 ==========
  MP_AUTO_REPLY_REQUEST_MATCH = 'mp_auto_reply_request_match', // 自动回复请求匹配类型
  MP_MESSAGE_TYPE = 'mp_message_type', // 消息类型

  // ========== Member 会员模块 ==========
  MEMBER_POINT_BIZ_TYPE = 'member_point_biz_type', // 积分的业务类型
  MEMBER_EXPERIENCE_BIZ_TYPE = 'member_experience_biz_type', // 会员经验业务类型

  // ========== MALL - 商品模块 ==========
  PRODUCT_SPU_STATUS = 'product_spu_status', //商品状态

  // ========== MALL - 交易模块 ==========
  EXPRESS_CHARGE_MODE = 'trade_delivery_express_charge_mode', //快递的计费方式
  TRADE_AFTER_SALE_STATUS = 'trade_after_sale_status', // 售后 - 状态
  TRADE_AFTER_SALE_WAY = 'trade_after_sale_way', // 售后 - 方式
  TRADE_AFTER_SALE_TYPE = 'trade_after_sale_type', // 售后 - 类型
  TRADE_ORDER_TYPE = 'trade_order_type', // 订单 - 类型
  TRADE_ORDER_STATUS = 'trade_order_status', // 订单 - 状态
  TRADE_ORDER_ITEM_AFTER_SALE_STATUS = 'trade_order_item_after_sale_status', // 订单项 - 售后状态
  TRADE_DELIVERY_TYPE = 'trade_delivery_type', // 配送方式
  BROKERAGE_ENABLED_CONDITION = 'brokerage_enabled_condition', // 分佣模式
  BROKERAGE_BIND_MODE = 'brokerage_bind_mode', // 分销关系绑定模式
  BROKERAGE_BANK_NAME = 'brokerage_bank_name', // 佣金提现银行
  BROKERAGE_WITHDRAW_TYPE = 'brokerage_withdraw_type', // 佣金提现类型
  BROKERAGE_RECORD_BIZ_TYPE = 'brokerage_record_biz_type', // 佣金业务类型
  BROKERAGE_RECORD_STATUS = 'brokerage_record_status', // 佣金状态
  BROKERAGE_WITHDRAW_STATUS = 'brokerage_withdraw_status', // 佣金提现状态
  TRADE_AREA_AGENT_LEAD_STATUS = 'trade_area_agent_lead_status', // 区域代理意向线索状态
  TRADE_ORDER_COMPLAINT_TYPE = 'trade_order_complaint_type', // 订单投诉类型

  // ========== MALL - 营销模块 ==========
  PROMOTION_DISCOUNT_TYPE = 'promotion_discount_type', // 优惠类型
  PROMOTION_PRODUCT_SCOPE = 'promotion_product_scope', // 营销的商品范围
  PROMOTION_COUPON_TEMPLATE_VALIDITY_TYPE = 'promotion_coupon_template_validity_type', // 优惠劵模板的有限期类型
  PROMOTION_COUPON_STATUS = 'promotion_coupon_status', // 优惠劵的状态
  PROMOTION_COUPON_TAKE_TYPE = 'promotion_coupon_take_type', // 优惠劵的领取方式
  PROMOTION_CONDITION_TYPE = 'promotion_condition_type', // 营销的条件类型枚举
  PROMOTION_BARGAIN_RECORD_STATUS = 'promotion_bargain_record_status', // 砍价记录的状态
  PROMOTION_COMBINATION_RECORD_STATUS = 'promotion_combination_record_status', // 拼团记录的状态
  PROMOTION_BANNER_POSITION = 'promotion_banner_position', // banner 定位

  // ========== CRM - 客户管理模块 ==========
  CRM_AUDIT_STATUS = 'crm_audit_status', // CRM 审批状态
  CRM_BIZ_TYPE = 'crm_biz_type', // CRM 业务类型
  CRM_BUSINESS_END_STATUS_TYPE = 'crm_business_end_status_type', // CRM 商机结束状态类型
  CRM_RECEIVABLE_RETURN_TYPE = 'crm_receivable_return_type', // CRM 回款的还款方式
  CRM_CUSTOMER_INDUSTRY = 'crm_customer_industry', // CRM 客户所属行业
  CRM_CUSTOMER_LEVEL = 'crm_customer_level', // CRM 客户级别
  CRM_CUSTOMER_SOURCE = 'crm_customer_source', // CRM 客户来源
  CRM_CUSTOMER_CATEGORY = 'crm_customer_category', // CRM 客户种类
  CRM_CUSTOMER_STATUS = 'crm_customer_status', // CRM 客户状态
  CRM_CUSTOMER_TYPE = 'crm_customer_type', // CRM 客户类型
  CRM_CUSTOMER_REQUIREMENT_CATEGORY = 'crm_customer_requirement_category', // CRM 需求分类
  CRM_CUSTOMER_PROJECT_APPROVED = 'crm_customer_project_approved', // CRM 项目是否立项
  CRM_PRODUCT_STATUS = 'crm_product_status', // CRM 商品状态
  CRM_PERMISSION_LEVEL = 'crm_permission_level', // CRM 数据权限的级别
  CRM_PRODUCT_UNIT = 'crm_product_unit', // CRM 产品单位
  CRM_PRODUCT_DEMO_STATUS = 'crm_product_demo_status', // CRM 产品演示站状态（访问正常/无法访问）
  CRM_PRODUCT_INDUSTRY = 'crm_product_industry', // CRM 产品行业
  CRM_PRODUCT_APPLICATION_SCENE = 'crm_product_application_scene', // CRM 产品应用场景
  CRM_FOLLOW_UP_TYPE = 'crm_follow_up_type', // CRM 跟进方式
  CRM_FOLLOW_UP_STATUS = 'crm_follow_up_status', // CRM 跟进状态（客户跟进）
  CRM_INTERVIEW_CATEGORY = 'crm_interview_category', // CRM 客户面谈分类
  CRM_PLAN_ASSIST_STATUS = 'crm_plan_assist_status', // CRM 方案协助完成状态（客户协同）
  CRM_BID_DEV_LANGUAGE = 'crm_bid_dev_language', // CRM 招标项目开发语言
  CRM_BID_PLATFORM_FEE_STANDARD = 'crm_bid_platform_fee_standard', // CRM 招标平台收费标准
  CRM_BID_PLATFORM_TYPE = 'crm_bid_platform_type', // CRM 招标平台类型
  CRM_ORDER_PROGRESS = 'crm_order_progress', // CRM 订单进度
  CRM_ORDER_STATUS = 'crm_order_status', // CRM 订单状态
  CRM_ORDER_STAGE = 'crm_order_stage', // CRM 订单阶段
  CRM_BID_PLATFORM_COOPERATION_STATUS = 'crm_bid_platform_cooperation_status', // CRM 招标平台合作状态
  // ★ 合同字段扩充（2026-09-21）新增的 4 个字典，见 sql/2026-09-21/crm-contract-extra-fields.sql
  CRM_CONTRACT_TYPE = 'crm_contract_type', // CRM 合同类型
  CRM_CONTRACT_PAYMENT_DIRECTION = 'crm_contract_payment_direction', // CRM 合同收/付款方向
  CRM_CONTRACT_PAYMENT_METHOD = 'crm_contract_payment_method', // CRM 合同付款方式
  CRM_CONTRACT_INSTALLMENT = 'crm_contract_installment', // CRM 合同付款分期
  // ★ 回款字段扩充（2026-09-22），见 sql/2026-09-22/crm-receivable-extra-fields.sql
  CRM_RECEIVABLE_RECEIVED_FLAG = 'crm_receivable_received_flag', // CRM 回款是否收款/付款

  // ========== ERP - 企业资源计划模块  ==========
  ERP_AUDIT_STATUS = 'erp_audit_status', // ERP 审批状态
  ERP_STOCK_RECORD_BIZ_TYPE = 'erp_stock_record_biz_type', // 库存明细的业务类型

  // ========== WMS - 仓库管理模块 ==========
  WMS_MERCHANT_TYPE = 'merchant_type', // WMS 往来企业类型
  WMS_ORDER_TYPE = 'wms_order_type', // WMS 单据类型
  WMS_ORDER_STATUS = 'wms_order_status', // WMS 单据状态
  WMS_RECEIPT_ORDER_TYPE = 'wms_receipt_order_type', // WMS 入库单类型
  WMS_SHIPMENT_ORDER_TYPE = 'wms_shipment_order_type', // WMS 出库单类型

  // ========== AI - 人工智能模块  ==========
  AI_PLATFORM = 'ai_platform', // AI 平台
  AI_MODEL_TYPE = 'ai_model_type', // AI 模型类型
  AI_IMAGE_STATUS = 'ai_image_status', // AI 图片状态
  AI_MUSIC_STATUS = 'ai_music_status', // AI 音乐状态
  AI_GENERATE_MODE = 'ai_generate_mode', // AI 生成模式
  AI_WRITE_TYPE = 'ai_write_type', // AI 写作类型
  AI_WRITE_LENGTH = 'ai_write_length', // AI 写作长度
  AI_WRITE_FORMAT = 'ai_write_format', // AI 写作格式
  AI_WRITE_TONE = 'ai_write_tone', // AI 写作语气
  AI_WRITE_LANGUAGE = 'ai_write_language', // AI 写作语言
  AI_IMAGE_PROMPT_WORDS = 'ai_image_prompt_words', // 图片提示词
  AI_VIDEO_STATUS = 'ai_video_status', // AI 视频状态
  AI_IMAGE_CATEGORY = 'ai_image_category', // 图片分类
  AI_CHAT_CATEGORY = 'ai_chat_category', // 问答分类
  AI_WRITE_CATEGORY = 'ai_write_category', // 创作分类
  AI_VIDEO_CATEGORY = 'ai_video_category', // 视频分类
  AI_MUSIC_CATEGORY = 'ai_music_category', // 音乐分类
  AI_PPT_CATEGORY = 'ai_ppt_category', // PPT分类
  AI_MODE3D_CATEGORY = 'ai_mode3d_category', // 3D 模型分类
  AI_AVATAR_CATEGORY = 'ai_avatar_category', // 数字人分类
  AI_WRITE_MODEL = 'ai_write_model', // 写作模型分类
  AI_MCP_CLIENT_NAME = 'ai_mcp_client_name', // AI MCP Client 名字

  // ========== IOT - 物联网模块  ==========
  IOT_NET_TYPE = 'iot_net_type', // IOT 联网方式
  IOT_PRODUCT_STATUS = 'iot_product_status', // IOT 产品状态
  IOT_PRODUCT_DEVICE_TYPE = 'iot_product_device_type', // IOT 产品设备类型
  IOT_PROTOCOL_TYPE = 'iot_protocol_type', // IOT 协议类型
  IOT_SERIALIZE_TYPE = 'iot_serialize_type', // IOT 序列化类型
  IOT_LOCATION_TYPE = 'iot_location_type', // IOT 定位类型
  IOT_DEVICE_STATE = 'iot_device_state', // IOT 设备状态
  IOT_THING_MODEL_TYPE = 'iot_thing_model_type', // IOT 产品功能类型
  IOT_THING_MODEL_UNIT = 'iot_thing_model_unit', // IOT 物模型单位
  IOT_RW_TYPE = 'iot_rw_type', // IOT 读写类型
  // TODO @芋艿：貌似这几个多了 _enum 后缀
  IOT_DATA_SINK_TYPE_ENUM = 'iot_data_sink_type_enum', // IoT 数据流转目的类型
  IOT_RULE_SCENE_TRIGGER_TYPE_ENUM = 'iot_rule_scene_trigger_type_enum', // IoT 场景流转的触发类型枚举
  IOT_RULE_SCENE_ACTION_TYPE_ENUM = 'iot_rule_scene_action_type_enum', // IoT 规则场景的触发类型枚举
  IOT_ALERT_LEVEL = 'iot_alert_level', // IoT 告警级别
  IOT_ALERT_RECEIVE_TYPE = 'iot_alert_receive_type', // IoT 告警接收类型
  IOT_OTA_TASK_DEVICE_SCOPE = 'iot_ota_task_device_scope', // IoT OTA任务设备范围
  IOT_OTA_TASK_STATUS = 'iot_ota_task_status', // IoT OTA 任务状态
  IOT_OTA_TASK_RECORD_STATUS = 'iot_ota_task_record_status', // IoT OTA 记录状态
  IOT_MODBUS_MODE = 'iot_modbus_mode', // IoT Modbus 工作模式
  IOT_MODBUS_FRAME_FORMAT = 'iot_modbus_frame_format', // IoT Modbus 帧格式

  // ========== MES - 制造执行系统模块  ==========
  MES_MD_ITEM_OR_PRODUCT = 'mes_md_item_or_product', // MES 物料产品标识
  MES_CLIENT_TYPE = 'mes_client_type', // MES 客户类型
  MES_VENDOR_LEVEL = 'mes_vendor_level', // MES 供应商级别
  MES_CAL_HOLIDAY_TYPE = 'mes_cal_holiday_type', // MES 假期类型
  MES_CAL_SHIFT_TYPE = 'mes_cal_shift_type', // MES 轮班方式
  MES_CAL_SHIFT_METHOD = 'mes_cal_shift_method', // MES 倒班方式
  MES_CAL_CALENDAR_TYPE = 'mes_cal_calendar_type', // MES 班组类型
  MES_CAL_PLAN_STATUS = 'mes_cal_plan_status', // MES 排班计划状态
  MES_TM_TOOL_STATUS = 'mes_tm_tool_status', // MES 工具状态
  MES_TM_MAINTEN_TYPE = 'mes_tm_mainten_type', // MES 保养维护类型
  MES_DV_MACHINERY_STATUS = 'mes_dv_machinery_status', // MES 设备状态
  MES_DV_SUBJECT_TYPE = 'mes_dv_subject_type', // MES 点检保养项目类型
  MES_INDICATOR_TYPE = 'mes_indicator_type', // MES 检测项类型
  MES_QC_RESULT_TYPE = 'mes_qc_result_type', // MES 质检结果值类型
  MES_DEFECT_LEVEL = 'mes_defect_level', // MES 缺陷等级
  MES_PRO_WORK_ORDER_STATUS = 'mes_pro_work_order_status', // MES 生产工单状态
  MES_PRO_WORK_ORDER_SOURCE_TYPE = 'mes_pro_work_order_source_type', // MES 工单来源类型
  MES_PRO_WORK_ORDER_TYPE = 'mes_pro_work_order_type', // MES 工单类型
  MES_QC_TYPE = 'mes_qc_type', // MES 质检方案类型
  MES_PRO_LINK_TYPE = 'mes_pro_link_type', // MES 工序关系类型
  MES_PRO_TASK_STATUS = 'mes_pro_task_status', // MES 生产任务状态
  MES_TIME_UNIT_TYPE = 'mes_time_unit_type', // MES 时间单位
  MES_ORDER_STATUS = 'mes_order_status', // MES 单据状态
  MES_QC_CHECK_RESULT = 'mes_qc_check_result', // MES 检测结果
  MES_QC_SOURCE_DOC_TYPE = 'mes_qc_source_doc_type', // MES 来源单据类型
  MES_IPQC_TYPE = 'mes_ipqc_type', // MES IPQC 检验类型
  MES_DV_CYCLE_TYPE = 'mes_dv_cycle_type', // MES 点检保养周期类型
  MES_DV_CHECK_PLAN_STATUS = 'mes_dv_check_plan_status', // MES 点检保养方案状态
  MES_MAINTEN_RECORD_STATUS = 'mes_mainten_record_status', // MES 保养记录状态
  MES_MAINTEN_STATUS = 'mes_mainten_status', // MES 保养结果
  MES_DV_REPAIR_STATUS = 'mes_dv_repair_status', // MES 维修工单状态
  MES_DV_REPAIR_RESULT = 'mes_dv_repair_result', // MES 维修结果
  MES_DV_CHECK_RECORD_STATUS = 'mes_dv_check_record_status', // MES 点检记录状态
  MES_DV_CHECK_RESULT = 'mes_dv_check_result', // MES 点检结果
  MES_PRO_FEEDBACK_STATUS = 'mes_pro_feedback_status', // MES 生产报工状态
  MES_PRO_FEEDBACK_TYPE = 'mes_pro_feedback_type', // MES 生产报工类型
  MES_PRO_FEEDBACK_CHANNEL = 'mes_pro_feedback_channel', // MES 生产报工途径
  MES_PRO_ANDON_STATUS = 'mes_pro_andon_status', // MES 安灯处置状态
  MES_PRO_ANDON_LEVEL = 'mes_pro_andon_level', // MES 安灯级别
  MES_PRO_WORK_RECORD_TYPE = 'mes_pro_work_record_type', // MES 上下工状态类型
  MES_RQC_TYPE = 'mes_rqc_type', // MES 退货检验类型
  MES_WM_ARRIVAL_NOTICE_STATUS = 'mes_wm_arrival_notice_status', // MES 到货通知单状态
  MES_WM_ITEM_RECEIPT_STATUS = 'mes_wm_item_receipt_status', // MES 物料接收单状态
  MES_WM_TRANSFER_STATUS = 'mes_wm_transfer_status', // MES 转移单状态
  MES_WM_TRANSFER_TYPE = 'mes_wm_transfer_type', // MES 转移单类型
  MES_WM_STOCK_TAKING_TYPE = 'mes_wm_stock_taking_type', // MES 盘点类型
  MES_WM_STOCK_TAKING_TASK_STATUS = 'mes_wm_stock_taking_task_status', // MES 盘点任务状态
  MES_WM_STOCK_TAKING_LINE_STATUS = 'mes_wm_stock_taking_task_line_status', // MES 盘点任务行状态
  MES_WM_STOCK_TAKING_PLAN_PARAM_TYPE = 'mes_wm_stock_taking_plan_param_type', // MES 盘点方案参数类型
  MES_WM_OUTSOURCE_RECPT_STATUS = 'mes_wm_outsource_recpt_status', // MES 外协入库单状态
  MES_WM_PRODUCT_ISSUE_STATUS = 'mes_wm_product_issue_status', // MES 领料出库单状态
  MES_WM_PRODUCT_PRODUCE_STATUS = 'mes_wm_product_produce_status', // MES 生产入库单状态
  MES_WM_RETURN_VENDOR_STATUS = 'mes_wm_return_vendor_status', // MES 供应商退货单状态
  MES_WM_QUALITY_STATUS = 'mes_wm_quality_status', // MES 质量状态
  MES_WM_RETURN_ISSUE_STATUS = 'mes_wm_return_issue_status', // MES 生产退料单状态
  MES_WM_RETURN_ISSUE_TYPE = 'mes_wm_return_issue_type', // MES 退料类型
  MES_WM_PRODUCT_RECPT_STATUS = 'mes_wm_product_receipt_status', // MES 成品入库单状态
  MES_WM_RETURN_SALES_STATUS = 'mes_wm_return_sales_status', // MES 销售退货单状态
  MES_WM_PRODUCT_SALES_STATUS = 'mes_wm_product_sales_status', // MES 销售出库单状态
  MES_WM_SALES_NOTICE_STATUS = 'mes_wm_sales_notice_status', // MES 发货通知单状态
  MES_WM_MISC_ISSUE_TYPE = 'mes_wm_misc_issue_type', // MES 杂项出库类型
  MES_WM_MISC_ISSUE_STATUS = 'mes_wm_misc_issue_status', // MES 杂项出库单状态
  MES_WM_MISC_RECEIPT_TYPE = 'mes_wm_misc_receipt_type', // MES 杂项单类型
  MES_WM_MISC_RECEIPT_STATUS = 'mes_wm_misc_receipt_status', // MES 杂项入库单状态
  MES_WM_OUTSOURCE_RECEIPT_STATUS = 'mes_wm_outsource_receipt_status', // MES 外协入库单状态
  MES_WM_OUTSOURCE_ISSUE_STATUS = 'mes_wm_outsource_issue_status', // MES 外协出库单状态
  MES_MD_AUTO_CODE_PART_TYPE = 'mes_md_auto_code_part_type', // MES 编码规则分段类型
  MES_MD_AUTO_CODE_PADDED_METHOD = 'mes_md_auto_code_padded_method', // MES 编码规则补齐方式
  MES_MD_AUTO_CODE_CYCLE_METHOD = 'mes_md_auto_code_cycle_method', // MES 编码规则循环方式
  MES_WM_BARCODE_FORMAT = 'mes_wm_barcode_format', // MES 条码格式
  MES_WM_BARCODE_BIZ_TYPE = 'mes_wm_barcode_biz_type', // MES 条码业务类型
  MES_WM_PACKAGE_STATUS = 'mes_wm_package_status', // MES 装箱单状态

  // ========== TRAIN - 课程培训模块  ==========
  TRAIN_PAPER_TYPE = 'train_paper_type', // 试卷类型：模拟/全真/冲刺
  TRAIN_COURSE_TYPE = 'train_course_type', // 课程类型：普通/取证
  TRAIN_CHARGE_TYPE = 'train_charge_type', // 收费方式：免费/付费/会员
  TRAIN_EXAM_MODE = 'train_exam_mode', // 考试模式：节考+总考/仅节考/仅总考
  TRAIN_COURSE_AUDIT_STATUS = 'train_course_audit_status', // 课程审核状态
  TRAIN_COURSE_STATUS = 'train_course_status', // 课程状态：上架/下架
  TRAIN_TEACHER_TYPE = 'train_teacher_type', // 老师类型：平台/个人/机构
  TRAIN_TEACHER_AUDIT_STATUS = 'train_teacher_audit_status', // 老师审核状态
  TRAIN_ORG_AUDIT_STATUS = 'train_org_audit_status', // 机构审核状态
  TRAIN_QUESTION_TYPE = 'train_question_type', // 题目类型：单选/多选/判断/填空/简答/综合
  TRAIN_DIFFICULTY = 'train_difficulty', // 题目难度：简单/中等/困难
  TRAIN_QUESTION_BANK_AUDIT_STATUS = 'train_question_bank_audit_status', // 题库档案审核状态
  TRAIN_AUDIT_TARGET_TYPE = 'train_audit_target_type', // 培训业务审计目标类型
  TRAIN_ORDER_TYPE = 'train_order_type', // 培训订单类型
  TRAIN_ORDER_STATUS = 'train_order_status', // 培训订单状态
  TRAIN_EXAM_TYPE = 'train_exam_type', // 培训考试类型
  TRAIN_LIVE_TYPE = 'train_live_type', // 培训直播类型
  TRAIN_LIVE_STATUS = 'train_live_status', // 培训直播状态
  TRAIN_ENROLL_SOURCE = 'train_enrollment_source', // 培训报名来源

  // ========== IM - 即时通讯模块  ==========
  IM_CONTENT_TYPE = 'im_content_type', // IM 内容类型
  IM_MESSAGE_STATUS = 'im_message_status', // IM 消息状态：0=正常 / 2=已撤回（私聊 / 群聊共用）
  IM_MESSAGE_RECEIPT_STATUS = 'im_message_receipt_status', // IM 消息回执状态：0=不需要 / 1=待完成 / 2=已完成
  IM_FRIEND_STATUS = 'im_friend_status', // IM 好友状态
  IM_FRIEND_ADD_SOURCE = 'im_friend_add_source', // IM 好友添加来源
  IM_FRIEND_REQUEST_HANDLE_RESULT = 'im_friend_request_handle_result', // IM 好友申请处理结果
  IM_GROUP_STATUS = 'im_group_status', // IM 群状态
  IM_GROUP_MEMBER_ROLE = 'im_group_member_role', // IM 群成员角色
  IM_GROUP_ADD_SOURCE = 'im_group_add_source', // IM 加群来源
  IM_GROUP_REQUEST_HANDLE_RESULT = 'im_group_request_handle_result', // IM 加群申请处理结果
  IM_RTC_CALL_MEDIA_TYPE = 'im_rtc_call_media_type', // IM 通话媒体类型：1=语音 / 2=视频
  IM_RTC_CALL_CONVERSATION_TYPE = 'im_rtc_call_conversation_type', // IM 通话会话类型：1=私聊 / 2=群聊
  IM_RTC_CALL_STATUS = 'im_rtc_call_status', // IM 通话状态：10=创建 / 20=进行中 / 30=已结束
  IM_RTC_CALL_END_REASON = 'im_rtc_call_end_reason', // IM 通话结束原因：1=通话结束 / 2=已拒绝 / 3=已取消 / 4=无人接听 / 5=对方正忙 / 9=通话异常
  IM_RTC_PARTICIPANT_ROLE = 'im_rtc_participant_role', // IM 通话参与角色：1=发起人 / 2=被邀请者 / 3=主动加入者
  IM_RTC_PARTICIPANT_STATUS = 'im_rtc_participant_status', // IM 通话参与状态：10=邀请中 / 20=已加入 / 30=已拒绝 / 40=未应答 / 50=已离开
  IM_CHANNEL_MATERIAL_TYPE = 'im_channel_material_type', // IM 频道素材内容类型：1=富文本 / 2=外链

  // ========== HOTEL - 酒店模块 ==========
  HOTEL_PROPERTY_TYPE = 'hotel_property_type', // 酒店物业类型
  HOTEL_EMPLOYMENT_TYPE = 'hotel_employment_type', // 雇佣类型
  HOTEL_EMPLOYEE_STATUS = 'hotel_employee_status', // 员工状态
  HOTEL_SCHEDULE_STATUS = 'hotel_schedule_status', // 排班状态
  HOTEL_PERFORMANCE_LEVEL = 'hotel_performance_level', // 绩效等级
  HOTEL_COMMISSION_TYPE = 'hotel_commission_type', // 提成类型
  HOTEL_COMMISSION_STATUS = 'hotel_commission_status', // 提成状态
  HOTEL_COMMISSION_RULE_STATUS = 'hotel_commission_rule_status', // 提成规则状态
  HOURLY_TEMPLATE_STATUS = 'hourly_template_status', // 钟点房模板状态
  HOTEL_EXTENSION_STATUS = 'hotel_extension_status', // 续房状态
  HOTEL_NIGHT_AUDIT_STATUS = 'hotel_night_audit_status', // 夜审状态
  HOTEL_REVIEW_STATUS = 'hotel_review_status', // 评价状态
  HOTEL_REVIEW_LEVEL = 'hotel_review_level', // 评价等级

  // ========== FOOD / CATERING - 餐饮模块 ==========
  CATERING_HOME_BANNER_POSITION = 'catering_home_banner_position', // 餐饮首页图片展示位置
  // FOOD_STORE_CATEGORY 已废弃：C 端侧栏改走业务表 catering_biz_category（管理端「业态分类」）

  // ========== MERCHANT - 商家模块 ==========
  MERCHANT_TYPE = 'merchant_apply_type', // 商家入驻类型（后台字典可维护；勿用 merchant_type，已被 WMS 往来企业类型占用）

  // ========== LAWYER - 律师平台 ==========
    LAWYER_CASE_TYPE = 'lawyer_case_type', // 案由类型（后台字典可维护）
  LAWYER_CUSTOMER_ID_TYPE = 'lawyer_customer_id_type', // 个人客户证件类型

  // ========== AGENT CHANNEL - 渠道代理商中台（与 system 代理商隔离） ==========
  AGENT_CHANNEL_TYPE = 'agent_channel_type', // 代理类型：个人/企业
  AGENT_CHANNEL_LEVEL = 'agent_channel_level', // 代理层级：一级/二级
  AGENT_CHANNEL_PROFILE_STATUS = 'agent_channel_profile_status', // 主档状态
  AGENT_CHANNEL_APPLY_STATUS = 'agent_channel_apply_status', // 申请单状态
  AGENT_CHANNEL_BANK = 'agent_channel_bank', // 开户行
  AGENT_CHANNEL_CLIENT_SOURCE = 'agent_channel_client_source', // 申请来源 pc/app/admin
  AGENT_CHANNEL_SOFTWARE = 'agent_channel_software', // 软件编码
  AGENT_CHANNEL_BASIS = 'agent_channel_basis', // 计费基数
  AGENT_CHANNEL_PERIOD = 'agent_channel_period', // 账期
  AGENT_CHANNEL_BIND_MODE = 'agent_channel_bind_mode', // 商家归属绑定方式
  AGENT_CHANNEL_SETTLE_STATUS = 'agent_channel_settle_status', // 结算单状态
  AGENT_CHANNEL_FIRST_ORDER_BONUS_MODE = 'agent_channel_first_order_bonus_mode', // 新客首单奖励方式
  AGENT_CHANNEL_REL_CHANGE_TYPE = 'agent_channel_rel_change_type', // 归属变更类型
  AGENT_CHANNEL_REL_CHANGE_STATUS = 'agent_channel_rel_change_status', // 归属变更状态
  AGENT_CHANNEL_LOG_BIZ_TYPE = 'agent_channel_log_biz_type', // 操作日志业务类型
  AGENT_CHANNEL_LOG_ACTION = 'agent_channel_log_action', // 操作日志动作

  // ========== EVENT - 赛事模块（枚举下拉一律字典，禁止写死） ==========
  EVENT_STATUS = 'event_status', // 赛事状态
  EVENT_MATCH_MODE = 'event_match_mode', // 赛制模式
  EVENT_TICKET_CHARGE_TYPE = 'event_ticket_charge_type', // 票种收费类型
  EVENT_TICKET_STATUS = 'event_ticket_status', // 票种上下架
  EVENT_SPECTATOR_TICKET_STATUS = 'event_spectator_ticket_status', // 观赛票状态
  EVENT_PARTY_TYPE = 'event_party_type', // 合作方类型
  EVENT_SPONSOR_LEVEL = 'event_sponsor_level', // 赞助等级
  EVENT_VOTE_TARGET = 'event_vote_target', // 投票目标
  EVENT_VOTE_POOL = 'event_vote_pool', // 投票票池
  EVENT_VOTE_RECORD_STATUS = 'event_vote_record_status', // 投票记录状态
  EVENT_SUBMISSION_STATUS = 'event_submission_status', // 作品状态
  EVENT_APPEAL_STATUS = 'event_appeal_status', // 申诉状态
  EVENT_JUDGE_SCORE_STATUS = 'event_judge_score_status', // 评委打分状态
  EVENT_SCHEDULE_STATUS = 'event_schedule_status', // 赛程状态
  EVENT_NEWS_STATUS = 'event_news_status', // 资讯状态
  EVENT_TEAM_STATUS = 'event_team_status', // 队伍状态
  EVENT_WAITING_STATUS = 'event_waiting_status', // 候补状态
  EVENT_TEMPLATE_SOURCE = 'event_template_source', // 模板来源
  EVENT_TEMPLATE_STATUS = 'event_template_status', // 模板状态
  EVENT_TEMPLATE_SCALE = 'event_template_scale', // 模板规模
  EVENT_TEMPLATE_TAG_TYPE = 'event_template_tag_type', // 模板标签类型
  EVENT_SCORING_METHOD = 'event_scoring_method', // 计分方式
  EVENT_SCORING_ORDER = 'event_scoring_order', // 计分排序
  EVENT_MEDAL = 'event_medal', // 奖牌
  EVENT_RESOURCE_TYPE = 'event_resource_type', // 资源类型
  EVENT_REGISTRATION_STATUS = 'event_registration_status', // 报名状态
  EVENT_GENDER = 'event_gender', // 性别
  EVENT_REFEREE_TYPE = 'event_referee_type', // 裁判类型
  EVENT_REFEREE_STATUS = 'event_referee_status', // 裁判分配状态
  EVENT_GLOBAL_MODE = 'event_global_mode', // 举办形态 线上/线下/混合
  EVENT_LOCATION_MODE = 'event_location_mode', // 赛区模式
  EVENT_REFEREE_CHECKIN_STATUS = 'event_referee_checkin_status', // 裁判签到状态
  EVENT_PATROL_STATUS = 'event_patrol_status', // 巡场状态
  EVENT_ORDER_STATUS = 'event_order_status', // 订单状态
  EVENT_AUDIT_STATUS = 'event_audit_status', // 资格审核状态
  EVENT_GUESS_STATUS = 'event_guess_status', // 竞猜状态
  EVENT_GUESS_OPTION = 'event_guess_option', // 竞猜结算选项
  EVENT_COMPLAINT_TYPE = 'event_complaint_type', // 投诉类型
  EVENT_COMPLAINT_STATUS = 'event_complaint_status', // 投诉处理状态
  EVENT_FEE_ITEM_TYPE = 'event_fee_item_type', // 费用类型
  EVENT_FEE_CHARGE_MODE = 'event_fee_charge_mode', // 费用计费单位
  EVENT_PARTICIPANT_CHECKIN_STATUS = 'event_participant_checkin_status', // 选手签到状态
  EVENT_CHECKIN_METHOD = 'event_checkin_method', // 签到方式
  EVENT_SPORT_TYPE = 'event_sport_type', // 运动类型
  EVENT_COMPETITION_FORMAT = 'event_competition_format', // 赛制形式
  EVENT_EVENT_TYPE = 'event_event_type', // 赛事类型
  EVENT_TIME_MODE = 'event_time_mode', // 时间维度
  EVENT_TIMEZONE = 'event_timezone', // 赛事时区（中文标签）
  EVENT_PARTICIPANT_STATUS = 'event_participant_status', // 选手确认状态
  EVENT_RESOURCE_STATUS = 'event_resource_status', // 资源启停
  EVENT_SCHEDULE_CONFLICT_SEVERITY = 'event_schedule_conflict_severity', // 赛程冲突级别
  EVENT_CHECKIN_STAGE = 'event_checkin_stage', // 签到阶段
  EVENT_RESULT_STATUS = 'event_result_status', // 成绩状态
  EVENT_MY_BUCKET = 'event_my_bucket', // 我的赛事分桶
  EVENT_CERTIFICATE_STATUS = 'event_certificate_status', // 证书状态
  EVENT_REVIEW_TARGET = 'event_review_target', // 赛后评价对象
  EVENT_REVIEW_STATUS = 'event_review_status', // 赛后评价状态
  EVENT_CREDIT_REASON = 'event_credit_reason', // 信用变动原因
  EVENT_BADGE_CODE = 'event_badge_code', // 勋章编码
  EVENT_INVOICE_STATUS = 'event_invoice_status', // 报名发票状态
  EVENT_INVOICE_TITLE_TYPE = 'event_invoice_title_type', // 发票抬头类型
  EVENT_MONITOR_SEVERITY = 'event_monitor_severity', // 执行监控异常级别
  EVENT_MONITOR_ANOMALY_TYPE = 'event_monitor_anomaly_type', // 执行监控异常类型
  EVENT_STAFF_TASK_STATUS = 'event_staff_task_status', // 工作人员任务状态
  EVENT_REFEREE_APPLY_STATUS = 'event_referee_apply_status', // 裁判申请状态
  EVENT_CHANNEL_STATUS = 'event_channel_status', // 渠道码状态
  EVENT_REFERRAL_STATUS = 'event_referral_status', // 推荐邀请状态
  EVENT_COMPLAINT_PRIORITY = 'event_complaint_priority', // 投诉优先级
  EVENT_RISK_ALERT_TYPE = 'event_risk_alert_type', // 风险告警类型
  EVENT_RISK_SEVERITY = 'event_risk_severity', // 风险告警级别
  EVENT_AGREEMENT_TYPE = 'event_agreement_type', // 协议类型 ENTRY/PRIVACY/HEALTH/CUSTOM
  EVENT_USER_ROLE = 'event_user_role', // 赛事用户身份 spectator/athlete/coach/referee/staff
  EVENT_ROLE_APPLY_STATUS = 'event_role_apply_status', // 身份申请状态
  EVENT_USER_ROLE_STATUS = 'event_user_role_status', // 身份授权启停
  LIVE_ROOM_STATUS = 'live_room_status', // 直播间状态

  // ========== AI 智课 / 大学 U-P0 ==========
  AI_EDU_STAGE = 'ai_edu_stage', // 学段
  AI_EDU_TERM_STATUS = 'ai_edu_term_status', // 学期状态
  AI_EDU_ENROLLMENT_STATUS = 'ai_edu_enrollment_status', // 选课状态
  AI_EDU_COMMON_STATUS = 'ai_edu_common_status', // 通用启停
  AI_EDU_WEEKDAY = 'ai_edu_weekday', // 课表星期
  AI_EDU_ATTENDANCE_SESSION_STATUS = 'ai_edu_attendance_session_status', // 考勤课次状态
  AI_EDU_ATTENDANCE_RESULT = 'ai_edu_attendance_result', // 考勤结果
  AI_EDU_ATTENDANCE_SOURCE = 'ai_edu_attendance_source', // 考勤来源
  AI_EDU_TEACH_SESSION_STATUS = 'ai_edu_teach_session_status', // 直播场次状态
  AI_EDU_GRADEBOOK_STATUS = 'ai_edu_gradebook_status', // 成绩册双审状态
  AI_EDU_SUBMISSION_STATUS = 'ai_edu_submission_status', // 作业提交状态
  AI_EDU_LEARNING_PLAN_STATUS = 'ai_edu_learning_plan_status', // 学习计划状态
  AI_EDU_LEARNING_PLAN_ITEM_STATUS = 'ai_edu_learning_plan_item_status', // 学习计划任务状态
  AI_EDU_PARENT_RELATIONSHIP = 'ai_edu_parent_relationship', // 家长亲属关系

  // ========== GEO 模块 ==========
  GEO_PUBLISH_CHANNEL = 'geo_publish_channel', // GEO 发布渠道
  GEO_PUBLISH_RECORD_STATUS = 'geo_publish_record_status', // GEO 发布记录状态
  GEO_MEDIA_CHANNEL_TYPE = 'geo_media_channel_type', // GEO 媒体站点渠道类型
  GEO_OPEN_PLATFORM = 'geo_open_platform', // GEO 开放平台社媒

  // ========== TMS - 智慧物流模块 ==========
  TMS_SETTLE_TYPE = 'tms_settle_type', // TMS 结算方式：1=月结 2=票结 3=预付
  TMS_VEHICLE_TYPE = 'tms_vehicle_type', // TMS 车型

  // ========== 评价系统模块（zijinshan-module-review） ==========
  REVIEW_SOURCE = 'review_source', // 业务来源：BOSS boss圈 / MALL 商城
  REVIEW_BIZ_SCENE = 'review_biz_scene', // 业务场景：ACTIVITY 活动 / EVENT 赛事 / SPACE 场地
  REVIEW_TARGET_TYPE = 'review_target_type', // 评价对象类型：ORGANIZER/VENUE/VENUE_OWNER/CO_ORGANIZER
  REVIEW_BIND_LEVEL = 'review_bind_level', // 评价对象绑定层级：0 仅名称 1 已绑企业 2 已绑发布人
  REVIEW_RECORD_STATUS = 'review_record_status', // 评价状态：0 正常 1 用户撤回 2 平台下架
  REVIEW_AUDIT_STATUS = 'review_audit_status', // 复核状态：0 免审 1 待复核 2 复核通过 10 复核不通过
  REVIEW_DISPLAY_STATUS = 'review_display_status', // 展示状态：0 隐藏 1 展示
  REVIEW_SORT_TYPE = 'review_sort_type', // 默认排序：NEWEST/USEFUL/RATING/COMPOSITE
  REVIEW_CREATE_SOURCE = 'review_create_source', // 创建来源：0 用户端提交 1 后台代录
  REVIEW_ADMIN_LOG_ACTION = 'review_admin_log_action', // 管理留痕动作：HIDE/SHOW/DELETE/AUDIT_PASS 等
  REVIEW_ENABLE_STATUS = 'review_enable_status', // 评价模块启用状态：0 停用 1 启用（与内核 COMMON_STATUS 语义相反）
  REVIEW_OPERATOR_TYPE = 'review_operator_type', // 留痕操作人类型：0 管理端 1 系统
  REVIEW_SETTING_SOURCE = 'review_setting_source', // 规则兜底链命中层级：EXACT/SCENE_DEFAULT/GLOBAL/FALLBACK

  // ========== 内容安全模块（zijinshan-module-contentsec） ==========
  CONTENTSEC_WORD_CATEGORY = 'contentsec_word_category', // 敏感词分类：POLITICS/ABUSE/AD/PORN/ILLEGAL_SERVICE/OTHER
  CONTENTSEC_WORD_LEVEL = 'contentsec_word_level', // 敏感词级别：1 高 2 中 3 低
  CONTENTSEC_MATCH_TYPE = 'contentsec_match_type', // 匹配方式：1 精确 2 模糊（尾部通配 *）
  CONTENTSEC_CHECK_VERDICT = 'contentsec_check_verdict', // 校验结论：0 通过 1 拦截 2 待人审 3 降级放行 4 插件跳过 5 脱敏放行
  CONTENTSEC_CHECK_ACTION = 'contentsec_check_action', // 处置动作：BLOCK 拦截 MASK 脱敏替换 REVIEW 先发后审
  CONTENTSEC_DEGRADE_POLICY = 'contentsec_degrade_policy', // 降级策略：PASS_MARK/REJECT/REVIEW
  CONTENTSEC_APPLY_FACE = 'contentsec_apply_face', // 生效接入面：APP 用户端 ADMIN 后台
  CONTENTSEC_PLUGIN_STATUS = 'contentsec_plugin_status', // 插件状态：0 停用 1 启用
  CONTENTSEC_CHECK_TRIGGER = 'contentsec_check_trigger', // 触发时机：SUBMIT 提交 RESCAN 回扫 TEST 在线测试
  CONTENTSEC_CONTENT_TYPE = 'contentsec_content_type', // 内容类型：1 文本 2 图片
  CONTENTSEC_WORD_LEVEL_THRESHOLD = 'contentsec_word_level_threshold', // 词级阈值：1 仅高级 2 中及以上 3 全部级别

  // ========== 投融资模块（zijinshan-module-invest，54 个 type 全部为字符串枚举，下拉一律用 getStrDictOptions） ==========
  // —— 项目与审核
  INVEST_PROJECT_STATUS = 'invest_project_status', // 项目状态：DRAFT/PENDING_AUDIT/PENDING_SUPPLEMENT/ONLINE/SUSPENDED/ARCHIVED/CLOSED
  INVEST_ROUND = 'invest_round', // 轮次：SEED/ANGEL/PRE_A/A/A_PLUS/B/C_PLUS/STRATEGY/OLD_SHARE
  INVEST_BIZ_CATEGORY = 'invest_biz_category', // 经营类型：PRODUCT/SERVICE/PLATFORM/LICENSE/CHAIN/ENGINEERING
  INVEST_AUDIT_STAGE = 'invest_audit_stage', // 审核阶段：MACHINE 机器初筛/FIRST 人工初审/SECOND 二次复审
  INVEST_AUDIT_STATUS = 'invest_audit_status', // 审核状态：PENDING/CLAIMED/PASS/REJECT/PENDING_DISCUSS
  INVEST_AUDIT_BIZ_TYPE = 'invest_audit_biz_type', // 审核对象：PROJECT/BP/CAPITAL/ROADSHOW/CASE/DEAL（除 PROJECT 外为预留）
  INVEST_REASON_CATEGORY = 'invest_reason_category', // 驳回理由分类：信息不实/承诺收益/涉众表述/材料缺失/财务存疑/计划书违规/疑似骗取/侵权/其他
  INVEST_CLOSE_REASON = 'invest_close_reason', // 关闭原因码：C-01..C-07
  INVEST_BIZ_DOMAIN = 'invest_biz_domain', // 配置域（理由码/参数分组）：match/search/project/audit/referral/auth/capital/contact/membership/roadshow/govern/ledger/dashboard
  // —— 资本方与机构
  INVEST_IDENTITY_TYPE = 'invest_identity_type', // 身份：ORG_MEMBER 机构从业人员/INDIVIDUAL 个人天使/GOV_STAFF 国资基金项目经理
  INVEST_ORG_TYPE = 'invest_org_type', // 机构类型：VC/PE/CVC/GOV/ANGEL_ORG
  INVEST_REAL_NAME_STATUS = 'invest_real_name_status', // 实名核验：UNVERIFIED/PASSED/FAILED
  INVEST_ORG_CONFIRM_STATUS = 'invest_org_confirm_status', // 机构确认：NONE/PENDING/CONFIRMED/REJECTED
  INVEST_ACCOUNT_STATUS = 'invest_account_status', // 账号状态：NORMAL/RESTRICTED/FROZEN/BLACKLISTED
  INVEST_INVESTING_STATUS = 'invest_investing_status', // 出手意愿：ACTIVE/WATCHING/PAUSED
  INVEST_ACCEPT_RELOCATION = 'invest_accept_relocation', // 是否接受异地：0 不接受/1 接受/2 可协商
  INVEST_CONTACT_LEVEL = 'invest_contact_level', // 联系方式强度：V0 隐藏/V1 占位/V2 脱敏/V3 明文
  INVEST_INTRO_SOURCE = 'invest_intro_source', // 简介来源：SELF 机构自填/ADMIN 后台代填
  INVEST_MATCH_FLAG = 'invest_match_flag', // 匹配度标记：LOW 偏低/NORMAL 正常
  INVEST_RECORD_STATUS = 'invest_record_status', // 机构公示核验：UNVERIFIED/VERIFIED/NOT_FOUND/POSTPONED
  // —— 计划书授权与阅读
  INVEST_AUTH_STATUS = 'invest_auth_status', // 授权状态：ACTIVE/REVOKED/EXPIRED/RESET
  INVEST_AUTH_SCOPE = 'invest_auth_scope', // 授权范围：FULL 全篇/PAGES 章节区间
  INVEST_AUTH_VALID_DAYS = 'invest_auth_valid_days', // 有效期：7/30/90 天
  INVEST_APPLY_STATUS = 'invest_apply_status', // BP 申请状态：PENDING/APPROVED/REJECTED/EXPIRED/FORWARDED
  INVEST_FEEDBACK_CONCLUSION = 'invest_feedback_conclusion', // 阅后结论：INTERESTED/WANT_MEETING/NEED_MORE_DOC/NOT_INTERESTED
  // —— 引荐
  INVEST_REFERRAL_STATUS = 'invest_referral_status', // 引荐单：PENDING_SEND/DELIVERED/VIEWED/INTERESTED/NOT_INTERESTED/TIMEOUT_CLOSED/CLOSED
  INVEST_REFERRAL_FEEDBACK = 'invest_referral_feedback', // 引荐反馈：MEET/MATERIAL/HOLD/GIVE_UP
  INVEST_SIDE = 'invest_side', // 主体侧：PROJECT 项目方/CAPITAL 资本方
  // —— 成交与案例
  INVEST_DEAL_STATUS = 'invest_deal_status', // 备案：PENDING/VERIFIED/REJECTED
  INVEST_DEAL_EVIDENCE = 'invest_deal_evidence', // 凭证类型：AGREEMENT 融资协议/COMMERCIAL 工商变更
  INVEST_CASE_PUBLISH_STATUS = 'invest_case_publish_status', // 案例：PENDING/PUBLISHED/OFFLINE
  INVEST_APPROVE_STATUS = 'invest_approve_status', // 通用审批：PENDING/APPROVED/REJECTED
  // —— 举报与处置
  INVEST_REPORT_STATUS = 'invest_report_status', // 工单：PENDING/ASSIGNED/DEFENDING/HANDLED/CLOSED/UNFOUNDED
  INVEST_REPORT_TYPE = 'invest_report_type', // 举报类型：FALSE_INFO/HARASSMENT/STEAL_BP/ILLEGAL_CHARGE/INFRINGEMENT/FALSE_AD
  INVEST_REPORT_ENTRY_SOURCE = 'invest_report_entry_source', // 举报入口：PROJECT_DETAIL/CAPITAL_PROFILE/SESSION/REFERRAL/ROADSHOW
  INVEST_REPORT_EVIDENCE_TYPE = 'invest_report_evidence_type', // 证据类型：SESSION/LOG/SNAPSHOT/FILE/DEFENSE
  INVEST_REPORT_HANDLED_RESULT = 'invest_report_handled_result', // 核实结论：FOUNDED 属实/UNFOUNDED 不属实
  INVEST_DISPOSAL_STATUS = 'invest_disposal_status', // 处置：ACTIVE/RELEASED/EXPIRED
  INVEST_DISPOSAL_LEVEL = 'invest_disposal_level', // 处置等级：WARNING/RESTRICT/FROZEN/BLACKLISTED
  INVEST_DISPOSAL_TARGET_TYPE = 'invest_disposal_target_type', // 处置对象：USER/CAPITAL/ORG/PROJECT
  INVEST_DISPOSAL_APPEAL_STATUS = 'invest_disposal_appeal_status', // 申诉：NONE/PENDING/GRANTED/REJECTED
  INVEST_RECTIFY_STATUS = 'invest_rectify_status', // 整改：NONE/DOING/DONE
  // —— 台账与合规
  INVEST_EXPORT_STATUS = 'invest_export_status', // 导出：PREPARED 已受理未交付/SUCCESS/FAILED
  INVEST_EXPORT_TYPE = 'invest_export_type', // 导出内容：LEDGER/QUALITY/ATTRIBUTION
  INVEST_SENSITIVE_TARGET_TYPE = 'invest_sensitive_target_type', // 明文查看对象：CAPITAL/PROJECT/ORG
  INVEST_DATA_RIGHTS_TYPE = 'invest_data_rights_type', // 个人信息权利：QUERY 查阅复制/CORRECT 更正补充/DELETE 删除
  INVEST_EXCHANGE_STATUS = 'invest_exchange_status', // 联系方式交换：PENDING/AGREED/REFUSED/EXPIRED/CANCELED
  // —— 路演
  INVEST_ROADSHOW_STATUS = 'invest_roadshow_status', // 场次：DRAFT/PUBLISHED/ONGOING/FINISHED/CANCELED/INVALID（后台作废置 INVALID，发布后不可回退草稿）
  INVEST_ROADSHOW_APPLY_STATUS = 'invest_roadshow_apply_status', // 报名材料：PENDING/SELECTED/REJECTED（后端常量 REVIEW_*，入选值是 SELECTED 不是 APPROVED）
  INVEST_ROADSHOW_INTENT = 'invest_roadshow_intent', // 现场意向：HIGH/LOW/NONE（48 小时未填由 Job 批量置 NONE）
  // —— 会员与配置
  INVEST_MEMBERSHIP_STATUS = 'invest_membership_status', // 订购：PENDING/ACTIVE/GRACE/EXPIRED/DOWNGRADED/REFUNDED/CANCELED
  INVEST_MEMBERSHIP_PLAN = 'invest_membership_plan', // 档位：BASIC/CERTIFIED/PREMIUM
  INVEST_QUOTA_KEY = 'invest_quota_key', // 配额项：BP_APPLY_DAY/BP_APPLY_WEEK/BROWSE_DAY/CONTACT_EXCHANGE_DAY/REFERRAL_MONTH
  INVEST_CONFIG_VALUE_TYPE = 'invest_config_value_type', // 参数值类型：STRING/INT/DECIMAL/LIST/JSON

  // ========== PMS - 项目管理模块 ==========
  PMS_PROJECT_TYPE = 'pms_project_type', // PMS 项目类型
  PMS_PROJECT_STATUS = 'pms_project_status', // PMS 项目状态
  PMS_PROJECT_LEVEL = 'pms_project_level', // PMS 项目优先级
  PMS_PROJECT_GROUP_TYPE = 'pms_project_group_type', // PMS 项目分组类型
  PMS_PROJECT_SCENE_TYPE = 'pms_project_scene_type', // PMS 项目列表场景
  PMS_PROJECT_SORT_TYPE = 'pms_project_sort_type', // PMS 项目排序类型
  PMS_PROJECT_MEMBER_LEVEL = 'pms_project_member_level', // PMS 项目成员权限级别
  PMS_ITERATION_STATUS = 'pms_iteration_status', // PMS 迭代状态
  PMS_WORK_ITEM_TYPE = 'pms_work_item_type', // PMS 工作项类型
  PMS_WORK_ITEM_STATUS_TYPE = 'pms_work_item_status_type', // PMS 工作项语义状态
  PMS_WORK_ITEM_LIFECYCLE_STATUS = 'pms_work_item_lifecycle_status', // PMS 工作项生命周期状态
  PMS_WORK_ITEM_PRIORITY = 'pms_work_item_priority', // PMS 工作项优先级
  PMS_WORK_ITEM_DEFECT_TYPE = 'pms_work_item_defect_type', // PMS 工作项缺陷类型
  PMS_KNOWLEDGE_OBJECT_TYPE = 'pms_knowledge_object_type', // PMS 知识对象类型
  PMS_KNOWLEDGE_DOCUMENT_TYPE = 'pms_knowledge_document_type', // PMS 知识文档类型
  PMS_KNOWLEDGE_DOCUMENT_STATUS = 'pms_knowledge_document_status', // PMS 知识文档状态
  PMS_KNOWLEDGE_CONTENT_LEVEL = 'pms_knowledge_content_level', // PMS 知识内容协作等级
  PMS_KNOWLEDGE_GROUP_TYPE = 'pms_knowledge_group_type', // PMS 知识库分组类型
  PMS_KNOWLEDGE_LIBRARY_MEMBER_LEVEL = 'pms_knowledge_library_member_level', // PMS 知识库成员等级

  // ========== OA - 办公自动化模块 ==========
  OA_PRIORITY = 'oa_priority', // OA 优先级
  OA_SCHEDULE_TYPE = 'oa_schedule_type', // OA 日程类型
  OA_PLAN_TYPE = 'oa_plan_type', // OA 计划类型
  OA_PLAN_STATUS = 'oa_plan_status', // OA 计划状态
  OA_WORK_REPORT_TYPE = 'oa_work_report_type', // OA 工作汇报类型
  OA_WORK_REPORT_STATUS = 'oa_work_report_status', // OA 工作汇报状态
  OA_NOTE_TYPE = 'oa_note_type', // OA 笔记类型
  OA_DISCUSSION_TYPE = 'oa_discussion_type', // OA 讨论类型
  OA_TASK_TYPE = 'oa_task_type', // OA 任务类型
  OA_TASK_STATUS = 'oa_task_status', // OA 任务状态
  OA_ANNOUNCEMENT_TYPE = 'oa_announcement_type', // OA 公告类型
  OA_ATTENDANCE_TYPE = 'oa_attendance_type', // OA 考勤类型
  OA_ATTENDANCE_STATUS = 'oa_attendance_status', // OA 考勤状态

  // ========== FMS - 财务管理模块 ==========
  FMS_ACCOUNT_USER_LEVEL = 'fms_account_user_level', // FMS 账套用户权限级别
  FMS_SUBJECT_CATEGORY = 'fms_subject_category', // FMS 科目类别
  FMS_DEBIT_CREDIT_DIRECTION = 'fms_debit_credit_direction', // FMS 借贷方向
  FMS_FINANCE_INDICATOR_TYPE = 'fms_finance_indicator_type', // FMS 财务指标取数报表类型
  FMS_SUBJECT_TYPE = 'fms_subject_type', // FMS 科目类型
  FMS_AUXILIARY_TYPE = 'fms_auxiliary_type', // FMS 辅助核算类别
  FMS_VOUCHER_STATUS = 'fms_voucher_status', // FMS 凭证状态
  FMS_VOUCHER_TIDY_TYPE = 'fms_voucher_tidy_type', // FMS 凭证整理方式
  FMS_FORMULA_RULE = 'fms_formula_rule', // FMS 报表公式取数规则
  FMS_REPORT_TYPE = 'fms_report_type', // FMS 财务报表类型
  FMS_REPORT_PERIOD_TYPE = 'fms_report_period_type', // FMS 财务报表期间类型
  FMS_LEDGER_BALANCE_MODE = 'fms_ledger_balance_mode', // FMS 账簿余额方向模式
  FMS_ACCOUNTING_STANDARD = 'fms_accounting_standard', // FMS 会计制度
  FMS_CLOSING_TYPE = 'fms_closing_type', // FMS 结账方案类型
  FMS_CLOSING_TIME_TYPE = 'fms_closing_time_type', // FMS 结账取数期间
  FMS_CLOSING_VOUCHER_TYPE = 'fms_closing_voucher_type', // FMS 结账凭证类型
  FMS_CLOSING_TEMPLATE_CATEGORY = 'fms_closing_template_category', // FMS 结账模板分类

  // ========== HRM - 人力资源模块 ==========
  HRM_EMPLOYEE_ENTRY_STATUS = 'hrm_employee_entry_status', // HRM 员工入职状态
  HRM_EMPLOYEE_STATUS = 'hrm_employee_status', // HRM 员工状态
  HRM_EMPLOYEE_TYPE = 'hrm_employee_type', // HRM 聘用形式
  HRM_EMPLOYEE_EDUCATION = 'hrm_employee_education', // HRM 员工学历
  HRM_RECRUIT_POST_STATUS = 'hrm_recruit_post_status', // HRM 招聘职位状态
  HRM_RECRUIT_JOB_NATURE = 'hrm_recruit_job_nature', // HRM 招聘工作性质
  HRM_RECRUIT_WORK_TIME = 'hrm_recruit_work_time', // HRM 招聘工作经验
  HRM_RECRUIT_POST_EDUCATION = 'hrm_recruit_post_education', // HRM 招聘职位学历要求
  HRM_RECRUIT_SALARY_UNIT = 'hrm_recruit_salary_unit', // HRM 招聘薪资单位
  HRM_RECRUIT_EMERGENCY_LEVEL = 'hrm_recruit_emergency_level', // HRM 招聘紧急程度
  HRM_RECRUIT_CANDIDATE_STATUS = 'hrm_recruit_candidate_status', // HRM 招聘候选人状态
  HRM_RECRUIT_CANDIDATE_EDUCATION = 'hrm_recruit_candidate_education', // HRM 招聘候选人学历
  HRM_RECRUIT_INTERVIEW_TYPE = 'hrm_recruit_interview_type', // HRM 招聘面试方式
  HRM_RECRUIT_INTERVIEW_RESULT = 'hrm_recruit_interview_result', // HRM 招聘面试结果
  HRM_ATTENDANCE_CLOCK_TYPE = 'hrm_attendance_clock_type', // HRM 打卡类型
  HRM_ATTENDANCE_CLOCK_SOURCE = 'hrm_attendance_clock_source', // HRM 打卡来源
  HRM_ATTENDANCE_CLOCK_STATUS = 'hrm_attendance_clock_status', // HRM 打卡状态
  HRM_ATTENDANCE_HOLIDAY_TYPE = 'hrm_attendance_holiday_type', // HRM 考勤节假日类型
  HRM_ATTENDANCE_LEAVE_TYPE = 'hrm_attendance_leave_type', // HRM 请假类型
  HRM_ATTENDANCE_YES_NO = 'hrm_attendance_yes_no', // HRM 考勤是否
  HRM_ATTENDANCE_LATE_EARLY_DEDUCT_METHOD = 'hrm_attendance_late_early_deduct_method', // HRM 迟到早退扣款方式
  HRM_ATTENDANCE_ABSENTEEISM_DEDUCT_METHOD = 'hrm_attendance_absenteeism_deduct_method', // HRM 旷工扣款方式
  HRM_ATTENDANCE_MISSCARD_DEDUCT_METHOD = 'hrm_attendance_misscard_deduct_method', // HRM 缺卡扣款方式
  HRM_SALARY_CHANGE_REASON = 'hrm_salary_change_reason', // HRM 薪资调整原因
  HRM_SALARY_CHANGE_TYPE = 'hrm_salary_change_type', // HRM 薪资档案状态
  HRM_SALARY_CHANGE_RECORD_STATUS = 'hrm_salary_change_record_status', // HRM 薪资调整记录状态
  HRM_SALARY_MONTH_STATUS = 'hrm_salary_month_status', // HRM 工资表状态
  HRM_SALARY_TAX_TYPE = 'hrm_salary_tax_type', // HRM 薪资计税类型
  HRM_SALARY_OPTION_TYPE = 'hrm_salary_option_type', // HRM 薪资项类型
  HRM_SALARY_SLIP_READ_STATUS = 'hrm_salary_slip_read_status', // HRM 工资条阅读状态
  HRM_SALARY_YES_NO = 'hrm_salary_yes_no', // HRM 薪资是否
  HRM_INSURANCE_SCHEME_TYPE = 'hrm_insurance_scheme_type', // HRM 社保方案类型
  HRM_INSURANCE_PROJECT_TYPE = 'hrm_insurance_project_type', // HRM 社保项目类型
  HRM_INSURANCE_MONTH_STATUS = 'hrm_insurance_month_status', // HRM 社保表状态
  HRM_INSURANCE_EMP_STATUS = 'hrm_insurance_emp_status', // HRM 参保状态
  HRM_PERFORMANCE_PLAN_STATUS = 'hrm_performance_plan_status', // HRM 绩效计划状态
  HRM_PERFORMANCE_STAGE_STATUS = 'hrm_performance_stage_status', // HRM 绩效阶段状态
  HRM_PERFORMANCE_ASSESSMENT_STAGE_STATUS = 'hrm_performance_assessment_stage_status', // HRM 绩效考核阶段处理状态
  HRM_PERFORMANCE_APPEAL_STATUS = 'hrm_performance_appeal_status', // HRM 绩效申诉状态
  HRM_PERFORMANCE_SCORE_CALCULATION = 'hrm_performance_score_calculation', // HRM 绩效计分方式
  HRM_PERFORMANCE_UPPER_LIMIT_TYPE = 'hrm_performance_upper_limit_type', // HRM 绩效上限类型
  HRM_PERFORMANCE_YES_NO = 'hrm_performance_yes_no', // HRM 绩效是否
  // ========== PJM 项目智能管理 ==========
  PJM_PERSON_STATUS = 'pjm_person_status', // 人物状态
  PJM_AGENT_STATUS = 'pjm_agent_status', // 智能体状态
  PJM_PROJECT_STATUS = 'pjm_project_status', // 项目状态
  PJM_TASK_STATUS = 'pjm_task_status', // 任务状态
  PJM_REQ_STATUS = 'pjm_req_status', // 需求状态
  PJM_JOB_STATUS = 'pjm_job_status', // 智能体任务状态
  PJM_REVIEW_VERDICT = 'pjm_review_verdict', // 评审结论
  PJM_RACI_ROLE = 'pjm_raci_role', // RACI 角色
}
